import {
  Button,
  Checkbox,
  DatePicker,
  ErrorBoundary,
  Input,
  Notice,
  Select,
  Text,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Skeleton } from 'native-base';
import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { NativeSyntheticEvent, TextInputChangeEventData } from 'react-native';
import { useRecoilState } from 'recoil';
import * as zod from 'zod';

import { useRecoilQuery } from '../../../../../../../../hooks/useRecoilQuery';
import { groupedCustomerDetailQuery } from '../../../../../../../../store/customerDetail/selector';
import { useWithdrawalActions } from '../../../../../../../../store/customerDetail/withdrawal/actions';
import { newWithdrawalAtom } from '../../../../../../../../store/customerDetail/withdrawal/atom';
import { customerWithdrawalsListBanksQuery } from '../../../../../../../../store/customerDetail/withdrawal/selector';
import { NewWithdrawalTypes } from '../../types';

export const AccountAndValue: React.FC<NewWithdrawalTypes.StepProps> = ({
  onNextStep,
  onCancel,
  accountId,
  accountNumber,
}) => {
  const [withdrawalState, setWithdrawalState] = useRecoilState(newWithdrawalAtom);
  const { isWithdrawalDateValid } = useWithdrawalActions();
  const {
    data: customerData,
    error: customerError,
    refresher: customerRefresher,
    loading: customerLoading,
  } = useRecoilQuery(groupedCustomerDetailQuery({ accountNumber }));

  const {
    data: bankListData,
    loading: bankListLoading,
    error: bankListError,
    refresher: bankListRefresher,
  } = useRecoilQuery(customerWithdrawalsListBanksQuery({ accountId }));

  const customerBalance = customerData?.accountAndEarnings.availableBalance || 0;
  const customerBalanceNormalized = Utils.Mask.Money(
    customerData?.accountAndEarnings.availableBalance
  );
  const customerBalanceInsufficient = customerBalance === 0;
  const customerHasNoBanks = bankListData?.length === 0;
  const disableAllControls = customerBalanceInsufficient || customerHasNoBanks;

  const validationSchema = useMemo(
    () =>
      zod
        .object({
          value: zod.string(),
          withdrawalDate: zod.date(),
          selectedBank: zod
            .number({ description: 'Escolha uma conta bancária para o destino do dinheiro.' })
            .gt(0, { message: 'Escolha uma conta bancária para o destino do dinheiro.' }),
        })
        .refine((data) => Utils.Unmask.Money(data.value) > 0, {
          path: ['value'],
          message: 'O valor do saque deve ser maior que zero.',
        })
        .refine((data) => Utils.Unmask.Money(data.value) <= customerBalance, {
          path: ['value'],
          message: `O valor do saque não pode ser maior que ${customerBalanceNormalized}.`,
        })
        .refine(
          (data) =>
            Utils.Date.IsFuture(data.withdrawalDate) || Utils.Date.IsToday(data.withdrawalDate),
          {
            path: ['withdrawalDate'],
            message: 'A data de saque deve ser maior ou igual a hoje.',
          }
        )
        .refine(
          async (data) => {
            const isValid = await isWithdrawalDateValid(data.withdrawalDate);
            return isValid;
          },
          {
            path: ['withdrawalDate'],
            message: 'A data de saque deve é inválida.',
          }
        ),
    [customerBalance, customerBalanceNormalized, isWithdrawalDateValid]
  );

  const {
    control,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm<zod.infer<typeof validationSchema>>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues: {
      value: Utils.Mask.Money(withdrawalState.value),
      selectedBank: withdrawalState.selectedBank,
      withdrawalDate: withdrawalState.withdrawalDate,
    },
  });

  const submitForm = handleSubmit(() => onNextStep('summaryAndTokenValidation'));

  const handleNextStep = useCallback(() => {
    submitForm();
  }, [submitForm]);

  const handleSelectDate = useCallback(
    (date: Date) => {
      setWithdrawalState((state) => ({ ...state, withdrawalDate: date }));
      setValue('withdrawalDate', date);
    },
    [setValue, setWithdrawalState]
  );

  const handleClearDate = useCallback(() => {
    setWithdrawalState((state) => ({ ...state, withdrawalDate: undefined }));
    // @ts-expect-error Date must be undefined
    setValue('withdrawalDate', undefined);
  }, [setValue, setWithdrawalState]);

  const handleValueChange = useCallback(
    (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
      setWithdrawalState((state) => ({ ...state, value: Utils.Unmask.Money(e.nativeEvent.text) }));
    },
    [setWithdrawalState]
  );

  const handleSelectBank = useCallback(
    (bankId: string) => {
      const cleanBankId = parseInt(bankId, 10);

      setWithdrawalState((state) => ({ ...state, selectedBank: cleanBankId }));
      setValue('selectedBank', cleanBankId);
    },
    [setValue, setWithdrawalState]
  );

  const handleRefresher = useCallback(() => {
    if (customerError) {
      customerRefresher();
    }

    if (bankListError) {
      bankListRefresher();
    }
  }, [customerError, bankListError, customerRefresher, bankListRefresher]);

  const handleWithdrawAll = useCallback(
    (_: string, selected: boolean) => {
      setWithdrawalState((state) => ({ ...state, withdrawAll: selected, value: customerBalance }));
      setValue('value', Utils.Mask.Money(customerBalance));
    },
    [customerBalance, setValue, setWithdrawalState]
  );

  const bankList =
    bankListData?.map((bank) => ({
      label: bank.fullBankName,
      value: bank.bankAccountId.toString(),
    })) ?? [];

  if (bankListError || customerError) {
    return <ErrorBoundary onCancel={onCancel} onRefresh={handleRefresher} />;
  }

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <VStack space={Spacing['stack.quarck']}>
        <HStack
          alignItems="center"
          padding={Spacing['inset.xs']}
          space={Spacing['stack.nano']}
          borderRadius={BoxRadius.sm}
          backgroundColor={
            customerBalanceInsufficient
              ? Utils.ColorToken.WithOpacity('feedback.error.lightest', 0.7)
              : Utils.ColorToken.WithOpacity('feedback.gen.lightest', 0.7)
          }>
          <Text color={customerBalanceInsufficient ? 'feedback.error.default' : 'neutral.black'}>
            Disponível para saque:
          </Text>
          {customerLoading ? (
            <Skeleton height={Spacing['inline.xxs']} borderRadius={BoxRadius.sm} flex={1} />
          ) : (
            <Text
              color={customerBalanceInsufficient ? 'feedback.error.default' : 'neutral.black'}
              fontWeight="semibold">
              {customerBalanceNormalized}
            </Text>
          )}
        </HStack>
        {customerBalanceInsufficient && (
          <Text color="feedback.error.default" fontSize={Typography['fontsize.xxs']}>
            Saldo insuficiente para realizar saque.
          </Text>
        )}
      </VStack>
      {customerHasNoBanks ? (
        <Notice
          textObjects={[
            { text: 'Atenção:', variant: 'semibold' },
            {
              text: ' O cliente informado não possui uma conta cadastrada. Para realizar o saque, é necessário que ele acesse o app e conclua o processo de solicitação.',
              variant: 'regular',
            },
          ]}
        />
      ) : (
        <>
          <Text fontWeight="semibold" fontSize={Typography['fontsize.md']}>
            Selecione a conta bancária de destino:
          </Text>
          {bankListLoading ? (
            <Skeleton height={Spacing['inline.xxs']} borderRadius={BoxRadius.sm} />
          ) : (
            <Select
              placeholder="Selecione a conta"
              options={bankList}
              disabled={disableAllControls}
              selectedValue={withdrawalState.selectedBank?.toString()}
              onValueChange={handleSelectBank}
              error={errors.selectedBank}
            />
          )}
        </>
      )}
      <HStack space={Spacing['stack.xxxs']} zIndex={1}>
        <VStack space={Spacing['stack.quarck']} flex={1}>
          <Text>Valor de saque:</Text>
          <Input
            // NOTE: Key is used to force re-render when withdrawAll changes
            control={control}
            name="value"
            autoComplete="off"
            value={withdrawalState.value.toString()}
            keyboardType="numeric"
            mask={Utils.Mask.InputMoney}
            onChange={handleValueChange}
            variant="filled"
            hasShadow={false}
            error={errors.value}
            isDisabled={disableAllControls || withdrawalState.withdrawAll}
          />
        </VStack>
        <VStack space={Spacing['stack.quarck']} flex={1}>
          <Text>Data para saque:</Text>
          <DatePicker
            date={withdrawalState.withdrawalDate}
            onSelectDate={handleSelectDate}
            onClear={handleClearDate}
            availableRange={{ past: new Date() }}
            error={errors.withdrawalDate}
            isDisabled={disableAllControls}
          />
        </VStack>
      </HStack>
      {customerLoading ? (
        <Skeleton height={Spacing['inline.xxs']} borderRadius={BoxRadius.sm} />
      ) : (
        <Checkbox
          testId="new-widthdraw-all-value"
          options={[{ value: '1', label: 'Solicitar valor total' }]}
          selectedValues={withdrawalState.withdrawAll ? ['1'] : []}
          onChange={handleWithdrawAll}
        />
      )}
      <VStack
        backgroundColor={Color.background}
        paddingX={Spacing['inset.sm']}
        paddingY={Spacing['inset.xs']}
        space={Spacing['stack.xxs']}
        borderRadius={BoxRadius.sm}>
        <Text fontWeight="semibold" fontSize={Typography['fontsize.md']}>
          HORÁRIO LIMITE
        </Text>
        <Text>
          Solicitações de transferências via TED solicitados até as 17h do dia corrente serão
          processados no mesmo dia. Solicitações realizadas após esse horário serão agendadas para o
          próximo dia útil.
        </Text>
        <Box justifyContent="flex-end">
          <Button
            onPress={handleNextStep}
            disabled={customerLoading || bankListLoading || disableAllControls}>
            Continuar
          </Button>
        </Box>
      </VStack>
    </VStack>
  );
};
